/* eslint-disable react/no-unescaped-entities */
import { useState, useMemo, useEffect } from "react";
import api from "../lib/api";
import { inr, inrFull } from "../lib/format";
import { toast } from "sonner";
import { Calculator, ChartBar, House, Bank, TrendUp, Receipt, Scales, Coins } from "@phosphor-icons/react";
import { CalcHeader, CalcDisclaimer, WhatsAppShare, NumField } from "../components/calculators/CalcShared";
import { CITY_DEFAULTS } from "../lib/dataFreshness";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

/* ======================================================================== */
/* P01 — Capital Gains Tax Calculator (Budget 2024 dual rate)              */
/* ======================================================================== */
export function CapitalGainsCalc() {
  const [form, setForm] = useState({
    purchase_price: 5000000, purchase_date: "2015-10-15",
    sale_price: 12000000, sale_date: "2024-12-15",
    purchase_expenses: 200000, sale_expenses: 100000,
    income_tax_slab_pct: 30, sec54_reinvest: 0, sec54ec_bonds: 0,
  });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calc/capital-gains", form);
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const shareMsg = res ? `🏠 Capital Gains Analysis (via Estima):
Sale: ${inr(form.sale_price)}, Purchase: ${inr(form.purchase_price)}
${res.classification} (${res.holding_years} yrs)
Tax payable: ${inr(res.scenarios?.find(s => s.recommended)?.total_tax)}` : "";

  return (
    <div className="space-y-6" data-testid="capgains-calc">
      <CalcHeader
        calc="capgains"
        title="Capital Gains Tax"
        subtitle="Budget 2024 dual rate: 12.5% no-indexation vs 20% with indexation — we auto-pick the cheaper.">
        {res && <WhatsAppShare calc="capgains" message={shareMsg} />}
      </CalcHeader>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="card-flat p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Purchase price (₹)" value={form.purchase_price}
              onChange={(v) => setForm({ ...form, purchase_price: v })}
              testid="capgains-purchase-price" />
            <label className="text-xs space-y-1 block">
              <span className="text-muted-foreground">Purchase date</span>
              <input type="date" value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                className="w-full bg-background border hairline px-2 py-1.5 text-xs"
                data-testid="capgains-purchase-date" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Sale price (₹)" value={form.sale_price}
              onChange={(v) => setForm({ ...form, sale_price: v })}
              testid="capgains-sale-price" />
            <label className="text-xs space-y-1 block">
              <span className="text-muted-foreground">Sale date</span>
              <input type="date" value={form.sale_date}
                onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                className="w-full bg-background border hairline px-2 py-1.5 text-xs"
                data-testid="capgains-sale-date" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Buy expenses (₹)" value={form.purchase_expenses}
              onChange={(v) => setForm({ ...form, purchase_expenses: v })}
              tooltip="Stamp duty + reg + brokerage at purchase" />
            <NumField label="Sale expenses (₹)" value={form.sale_expenses}
              onChange={(v) => setForm({ ...form, sale_expenses: v })}
              tooltip="Brokerage + legal at sale" />
          </div>
          <NumField label="Income tax slab % (for STCG)" value={form.income_tax_slab_pct}
            step="0.1" onChange={(v) => setForm({ ...form, income_tax_slab_pct: v })} />
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Sec 54 reinvest (₹)" value={form.sec54_reinvest}
              onChange={(v) => setForm({ ...form, sec54_reinvest: v })}
              tooltip="Reinvested in residential property within 2 years" />
            <NumField label="Sec 54EC bonds (₹)" value={form.sec54ec_bonds}
              onChange={(v) => setForm({ ...form, sec54ec_bonds: v })}
              tooltip="NHAI/REC bonds; max ₹50L" max={5000000} />
          </div>
          <button onClick={run} disabled={loading}
            className="btn-primary w-full py-2.5 text-sm mt-2"
            data-testid="capgains-run">
            {loading ? "Calculating…" : "Calculate"}
          </button>
        </div>

        <div>
          {!res ? (
            <div className="card-flat p-8 text-muted-foreground">Enter values and hit calculate.</div>
          ) : (
            <div className="space-y-4" data-testid="capgains-result">
              <div className="card-flat p-5 flex items-baseline gap-3 flex-wrap">
                <div>
                  <div className="eyebrow">Classification</div>
                  <div className="font-serif text-2xl mt-1">
                    {res.classification}
                    <span className="text-xs text-muted-foreground ml-2">({res.holding_years} yrs held)</span>
                  </div>
                </div>
                <div className="ml-auto text-xs">
                  Cost base: {inr(res.cost_base)} · Net sale: {inr(res.sale_net)}
                  {res.indexed_cost_if_applicable != null && (
                    <span> · Indexed cost: {inr(res.indexed_cost_if_applicable)}</span>
                  )}
                </div>
              </div>

              <div className={`grid gap-3 ${res.scenarios.length === 2 ? "md:grid-cols-2" : ""}`}>
                {res.scenarios.map((s, i) => (
                  <div key={s.type || s.label || i}
                    className={`card-flat p-5 border-2 ${
                      s.recommended ? "border-green-500" : "border-transparent"
                    }`}
                    data-testid={`capgains-scenario-${i}`}>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-medium">{s.label}</div>
                      {s.recommended && (
                        <span className="bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                          ✓ Recommended
                        </span>
                      )}
                    </div>
                    <div className="font-serif text-3xl mt-2">{inr(s.total_tax)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Effective rate: {((s.total_tax / (s.gain || 1)) * 100).toFixed(1)}%
                    </div>
                    <div className="mt-3 text-xs grid grid-cols-2 gap-1">
                      <span className="text-muted-foreground">Gain (gross):</span>
                      <span className="text-right">{inr(s.gain)}</span>
                      {s.indexed_cost != null && (<>
                        <span className="text-muted-foreground">Indexed cost (CII {s.cii_purchase_year}→{s.cii_sale_year}):</span>
                        <span className="text-right">{inr(s.indexed_cost)}</span>
                      </>)}
                      {s.gain_after_exemption !== s.gain && (<>
                        <span className="text-muted-foreground">After exemption:</span>
                        <span className="text-right">{inr(s.gain_after_exemption)}</span>
                      </>)}
                      <span className="text-muted-foreground">Tax @ {s.rate_pct}%:</span>
                      <span className="text-right">{inr(s.base_tax)}</span>
                      <span className="text-muted-foreground">Surcharge:</span>
                      <span className="text-right">{inr(s.surcharge)}</span>
                      <span className="text-muted-foreground">Cess (4%):</span>
                      <span className="text-right">{inr(s.cess)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card-flat p-3 text-xs bg-green-500/10 border border-green-500/30"
                data-testid="capgains-recommendation">
                <b className="text-green-700">Recommendation:</b> {res.recommendation}
              </div>
              <div className="text-[10px] text-muted-foreground">
                CII table per CBDT notification (FY 2025-26). Section refs: ITA 1961 §48, §54, §54EC, §54F. Finance Act 2024 (effective 23-Jul-2024).
              </div>
              <CalcDisclaimer calc="capgains" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ======================================================================== */
/* P02 — Stamp Duty & Registration                                          */
/* ======================================================================== */
export function StampDutyCalc() {
  const [form, setForm] = useState({
    state: "MH", sale_price: 8000000, circle_rate_value: 0,
    gender: "male", is_under_construction: false, is_affordable_housing: false,
    brokerage_pct: 1.0, legal_costs: 25000,
  });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calc/stamp-duty", {
        ...form,
        circle_rate_value: form.circle_rate_value || null,
      });
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  const shareMsg = res ? `🏠 Stamp Duty Analysis (via Estima):
${res.state_label} · Sale: ${inr(form.sale_price)}
Stamp duty: ${inr(res.stamp_duty)}
Registration: ${inr(res.registration)}
${res.gst_applicable ? `GST: ${inr(res.gst_amount)}` : ""}
TOTAL acquisition: ${inr(res.total_acquisition_cost)}` : "";

  return (
    <div className="space-y-6" data-testid="stampduty-calc">
      <CalcHeader calc="stampduty" title="Stamp Duty & Registration"
        subtitle="Computed on the HIGHER of sale price or government circle rate. GST flagged for under-construction.">
        {res && <WhatsAppShare calc="stampduty" message={shareMsg} />}
      </CalcHeader>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="card-flat p-5 space-y-3">
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">State</span>
            <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="stampduty-state">
              {["MH","KA","DL","TS","TN","UP","GJ","WB","RJ","HR"].map((s) =>
                <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <NumField label="Sale price (₹)" value={form.sale_price}
            onChange={(v) => setForm({ ...form, sale_price: v })}
            testid="stampduty-sale-price" />
          <NumField label="Circle rate value (₹) — optional" value={form.circle_rate_value}
            onChange={(v) => setForm({ ...form, circle_rate_value: v })}
            tooltip="Govt minimum value per IGRS portal. If higher than sale price, stamp duty is computed on this."
            testid="stampduty-circle-rate" />
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Buyer gender (for concession)</span>
            <div className="inline-flex border hairline rounded overflow-hidden text-xs">
              {[["male", "Male"], ["female", "Female"]].map(([v, l]) => (
                <button key={v}
                  onClick={() => setForm({ ...form, gender: v })}
                  data-testid={`stampduty-gender-${v}`}
                  className={`px-3 py-1.5 ${form.gender === v ? "bg-[hsl(var(--primary))] text-white" : ""}`}>
                  {l}
                </button>
              ))}
            </div>
          </label>
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Property status</span>
            <div className="inline-flex border hairline rounded overflow-hidden text-xs">
              <button onClick={() => setForm({ ...form, is_under_construction: false })}
                className={`px-3 py-1.5 ${!form.is_under_construction ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                data-testid="stampduty-rtm">Ready-to-Move</button>
              <button onClick={() => setForm({ ...form, is_under_construction: true })}
                className={`px-3 py-1.5 ${form.is_under_construction ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                data-testid="stampduty-uc">Under-Construction</button>
            </div>
          </label>
          {form.is_under_construction && (
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={form.is_affordable_housing}
                onChange={(e) => setForm({ ...form, is_affordable_housing: e.target.checked })}
                data-testid="stampduty-affordable" />
              Affordable housing (≤₹45L → 1% GST)
            </label>
          )}
          <NumField label="Brokerage %" value={form.brokerage_pct} step="0.1"
            onChange={(v) => setForm({ ...form, brokerage_pct: v })} />
          <NumField label="Legal / documentation (₹)" value={form.legal_costs}
            onChange={(v) => setForm({ ...form, legal_costs: v })} />
          <button onClick={run} disabled={loading}
            className="btn-primary w-full py-2.5 text-sm mt-2"
            data-testid="stampduty-run">{loading ? "Calculating…" : "Calculate"}</button>
        </div>
        <div>
          {!res ? (
            <div className="card-flat p-8 text-muted-foreground">Enter values and hit calculate.</div>
          ) : (
            <div className="space-y-4" data-testid="stampduty-result">
              {res.base_above_sale_price && (
                <div className="bg-blue-500/10 border border-blue-500/30 text-blue-700 text-xs p-3"
                  data-testid="stampduty-circle-warning">
                  Stamp duty is computed on the circle rate ({inr(res.stamp_duty_base)}) which is higher than your sale price ({inr(res.sale_price)}). This is standard practice per state stamp duty laws.
                </div>
              )}
              {res.warnings?.map((w, i) => (
                <div key={i} className="bg-amber-500/10 border border-amber-500/30 text-amber-700 text-xs p-3">
                  ⚠ {w}
                </div>
              ))}
              {res.gst_applicable && (
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-700 text-xs p-3">
                  <b>GST applicable</b>: {res.gst_rate_pct}% on the construction portion (~2/3 of agreement value).
                </div>
              )}
              <div className="card-flat p-5">
                <div className="eyebrow mb-3">Total acquisition cost</div>
                <div className="font-serif text-4xl text-[hsl(var(--primary))]" data-testid="stampduty-total">
                  {inrFull(res.total_acquisition_cost)}
                </div>
                <table className="w-full text-xs mt-4">
                  <tbody>
                    <Row k="Property / sale price" v={inr(res.sale_price)} />
                    <Row k={`Stamp duty (${res.stamp_duty_rate_pct}%${res.gender_concession_applied ? ", women" : ""})`} v={inr(res.stamp_duty)} />
                    <Row k={`Registration (${res.registration_rate_pct}%)`} v={inr(res.registration)} />
                    {res.gst_applicable && <Row k={`GST (${res.gst_rate_pct}%)`} v={inr(res.gst_amount)} />}
                    <Row k="Brokerage" v={inr(res.brokerage)} />
                    <Row k="Legal / docs" v={inr(res.legal_costs)} />
                  </tbody>
                </table>
              </div>
              <div className="card-flat p-3 text-xs bg-teal-500/10 border border-teal-500/30">
                <b>Section 80C:</b> Stamp duty + registration paid on new residential property
                is deductible under §80C up to ₹1.5L in year of payment. Eligible amount here:
                <b className="ml-1">{inr(res.sec80c_eligible)}</b>
              </div>
              <CalcDisclaimer calc="stampduty" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr className="border-t hairline">
      <td className="py-1.5 text-muted-foreground">{k}</td>
      <td className="py-1.5 text-right font-medium">{v}</td>
    </tr>
  );
}

/* ======================================================================== */
/* P03 — Rental Yield & ROI (NAV-correct)  +  P07 REIT comparator inside    */
/* ======================================================================== */
export function RentalYieldCalc() {
  const [tab, setTab] = useState("yield");
  return (
    <div className="space-y-6" data-testid="rental-calc">
      <CalcHeader calc="rental" title="Rental Yield & ROI"
        subtitle="Section 24A standard deduction on Net Annual Value (NAV), Section 24B interest. REIT comparator built-in." />
      <div className="inline-flex border hairline rounded overflow-hidden text-xs">
        <button onClick={() => setTab("yield")}
          className={`px-3 py-1.5 ${tab === "yield" ? "bg-[hsl(var(--primary))] text-white" : ""}`}
          data-testid="rental-tab-yield">Yield & ROI</button>
        <button onClick={() => setTab("reit")}
          className={`px-3 py-1.5 ${tab === "reit" ? "bg-[hsl(var(--primary))] text-white" : ""}`}
          data-testid="rental-tab-reit">REIT vs Direct</button>
      </div>
      {tab === "yield" ? <YieldTab /> : <ReitTab />}
    </div>
  );
}

function YieldTab() {
  const [f, setF] = useState({
    property_price: 8000000, monthly_rent: 25000, vacancy_months: 1,
    municipal_tax: 12000, home_loan_interest_annual: 0, is_let_out: true,
    income_tax_slab: 30,
  });
  const r = useMemo(() => {
    const effectiveGAR = f.monthly_rent * (12 - (f.vacancy_months || 0));
    const nav = Math.max(0, effectiveGAR - (f.municipal_tax || 0));
    const stdDeduction = nav * 0.30;
    const interestDeduction = f.is_let_out ? (f.home_loan_interest_annual || 0)
                                            : Math.min(200000, f.home_loan_interest_annual || 0);
    const taxableHouseProperty = nav - stdDeduction - interestDeduction;
    const tax = Math.max(0, taxableHouseProperty * (f.income_tax_slab / 100));
    const netAfterTax = effectiveGAR - tax;
    const grossYield = (effectiveGAR / f.property_price) * 100;
    const netYield = (netAfterTax / f.property_price) * 100;
    return { effectiveGAR, nav, stdDeduction, interestDeduction, taxableHouseProperty, tax, netAfterTax, grossYield, netYield };
  }, [f]);
  const shareMsg = `🏠 Rental Yield (via Estima):
Property: ${inr(f.property_price)}, Rent: ${inr(f.monthly_rent)}/mo
Gross yield: ${r.grossYield.toFixed(2)}%
Net post-tax yield: ${r.netYield.toFixed(2)}%`;
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <div className="card-flat p-5 space-y-3">
        <NumField label="Property price (₹)" value={f.property_price} onChange={(v) => setF({...f, property_price: v})}/>
        <NumField label="Monthly rent (₹)" value={f.monthly_rent} onChange={(v) => setF({...f, monthly_rent: v})}/>
        <NumField label="Vacancy (months/year)" value={f.vacancy_months} onChange={(v) => setF({...f, vacancy_months: v})}
          tooltip="Default 1. We deduct vacancy before computing effective GAR." />
        <NumField label="Municipal tax / year (₹)" value={f.municipal_tax} onChange={(v) => setF({...f, municipal_tax: v})}
          tooltip="Property tax paid to your local body (BBMP/BMC/MCD etc.). Reduces NAV before 30% standard deduction." />
        <NumField label="Home loan interest / year (₹)" value={f.home_loan_interest_annual} onChange={(v) => setF({...f, home_loan_interest_annual: v})}/>
        <label className="text-xs space-y-1 block">
          <span className="text-muted-foreground">Property use</span>
          <div className="inline-flex border hairline rounded overflow-hidden text-xs">
            <button onClick={() => setF({...f, is_let_out: false})}
              className={`px-3 py-1.5 ${!f.is_let_out ? "bg-[hsl(var(--primary))] text-white" : ""}`}>Self-Occ (₹2L cap)</button>
            <button onClick={() => setF({...f, is_let_out: true})}
              className={`px-3 py-1.5 ${f.is_let_out ? "bg-[hsl(var(--primary))] text-white" : ""}`}>Let-Out (no cap)</button>
          </div>
        </label>
        <NumField label="Income tax slab %" value={f.income_tax_slab} step="0.1" onChange={(v) => setF({...f, income_tax_slab: v})}/>
      </div>
      <div className="space-y-4" data-testid="rental-yield-result">
        <div className="grid md:grid-cols-3 gap-3">
          <Metric label="Gross yield" value={`${r.grossYield.toFixed(2)}%`} testid="rental-gross-yield"/>
          <Metric label="Net yield (post-tax)" value={`${r.netYield.toFixed(2)}%`} accent="good" testid="rental-net-yield"/>
          <Metric label="Annual tax" value={inr(r.tax)} testid="rental-tax"/>
        </div>
        <div className="card-flat p-5">
          <div className="eyebrow mb-3">How rental tax is computed (Sections 22-27 ITA)</div>
          <table className="w-full text-xs">
            <tbody>
              <Row k={`Effective Gross Annual Rent (${12 - (f.vacancy_months || 0)} months)`} v={inr(r.effectiveGAR)}/>
              <Row k="Less: Municipal taxes paid" v={`(${inr(f.municipal_tax || 0)})`}/>
              <Row k="Net Annual Value (NAV)" v={inr(r.nav)}/>
              <Row k="Less: Standard deduction @ 30% of NAV (§24A)" v={`(${inr(r.stdDeduction)})`}/>
              <Row k={`Less: Home loan interest (§24B${f.is_let_out ? "" : ", capped ₹2L"})`} v={`(${inr(r.interestDeduction)})`}/>
              <Row k="Taxable income from house property" v={inr(r.taxableHouseProperty)}/>
              <Row k={`Tax @ ${f.income_tax_slab}% slab`} v={inr(r.tax)}/>
              <Row k="Net rental income after tax" v={inr(r.netAfterTax)}/>
            </tbody>
          </table>
        </div>
        <div className="flex gap-2"><WhatsAppShare calc="rental" message={shareMsg}/></div>
        <CalcDisclaimer calc="rental" />
      </div>
    </div>
  );
}

function ReitTab() {
  const [f, setF] = useState({
    capital: 2500000, property_price: 10000000, monthly_rent: 25000,
    appreciation: 7, rent_increase: 5, loan_rate: 8.75, tenure_years: 20,
    reit_yield: 7, reit_growth: 5, tax_slab: 30,
  });
  const r = useMemo(() => {
    const loan = Math.max(0, f.property_price - f.capital);
    const months = f.tenure_years * 12;
    const mRate = f.loan_rate / 100 / 12;
    const emi = mRate > 0 ? (loan * mRate * Math.pow(1 + mRate, months)) / (Math.pow(1 + mRate, months) - 1) : loan / months;
    // 20-year property: avg rent (geometric mean of years), tax on rental as let-out at slab
    let avgRent = 0;
    for (let y = 0; y < f.tenure_years; y++) {
      avgRent += f.monthly_rent * 12 * Math.pow(1 + f.rent_increase / 100, y);
    }
    avgRent = avgRent / f.tenure_years;
    const propEndValue = f.property_price * Math.pow(1 + f.appreciation / 100, f.tenure_years);
    const totalEmiPaid = emi * months;
    const taxOnRent = avgRent * f.tenure_years * 0.7 * (f.tax_slab / 100);   // approx after Sec 24A
    const propertyNetEquity = propEndValue - taxOnRent - totalEmiPaid + (avgRent * f.tenure_years);
    // REIT
    const annualDist = f.capital * (f.reit_yield / 100);
    const reitEndNAV = f.capital * Math.pow(1 + f.reit_growth / 100, f.tenure_years);
    const reitNetDist = annualDist * (1 - f.tax_slab / 100);
    const reitTotalReturn = reitEndNAV + reitNetDist * f.tenure_years;
    // XIRR approx — simple CAGR over horizon (guard against negative equity → NaN)
    const safePropEquity = Math.max(1, propertyNetEquity);
    const safeReitReturn = Math.max(1, reitTotalReturn);
    const propertyXirr = ((safePropEquity / Math.max(1, f.capital)) ** (1 / f.tenure_years) - 1) * 100;
    const reitXirr = ((safeReitReturn / Math.max(1, f.capital)) ** (1 / f.tenure_years) - 1) * 100;
    return { loan, emi, avgRent, propEndValue, propertyNetEquity, propertyXirr,
             annualDist, reitNetDist, reitEndNAV, reitTotalReturn, reitXirr };
  }, [f]);
  const winner = r.propertyXirr > r.reitXirr ? "property" : "reit";
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6" data-testid="reit-tab">
      <div className="card-flat p-5 space-y-3">
        <NumField label="Investment capital (₹)" value={f.capital} onChange={(v) => setF({...f, capital: v})}/>
        <div className="font-medium text-xs mt-2 text-muted-foreground">→ Direct Property</div>
        <NumField label="Property price (₹)" value={f.property_price} onChange={(v) => setF({...f, property_price: v})}/>
        <NumField label="Monthly rent (₹)" value={f.monthly_rent} onChange={(v) => setF({...f, monthly_rent: v})}/>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Loan %" value={f.loan_rate} step="0.05" onChange={(v) => setF({...f, loan_rate: v})}/>
          <NumField label="Tenure (yrs)" value={f.tenure_years} onChange={(v) => setF({...f, tenure_years: v})}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Appreciation %" value={f.appreciation} step="0.1" onChange={(v) => setF({...f, appreciation: v})}/>
          <NumField label="Rent ↑ %" value={f.rent_increase} step="0.1" onChange={(v) => setF({...f, rent_increase: v})}/>
        </div>
        <div className="font-medium text-xs mt-2 text-muted-foreground">→ REIT</div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Distrib yield %" value={f.reit_yield} step="0.1" onChange={(v) => setF({...f, reit_yield: v})}/>
          <NumField label="NAV growth %" value={f.reit_growth} step="0.1" onChange={(v) => setF({...f, reit_growth: v})}/>
        </div>
        <NumField label="Income tax slab %" value={f.tax_slab} step="1" onChange={(v) => setF({...f, tax_slab: v})}/>
      </div>
      <div className="space-y-4" data-testid="reit-result">
        <div className="grid md:grid-cols-2 gap-4">
          <ScenarioCard title="Direct Property" winner={winner === "property"}
            metrics={[
              ["Capital deployed", inr(f.capital), "Down payment only"],
              ["Loan taken", inr(r.loan), `${f.tenure_years}-yr @ ${f.loan_rate}%`],
              ["Monthly EMI", inr(r.emi), null],
              [`End value (yr ${f.tenure_years})`, inr(r.propEndValue), null],
              ["Net equity", inr(r.propertyNetEquity), null],
              ["Effective XIRR (approx)", `${r.propertyXirr.toFixed(2)}%`, "p.a."],
              ["Liquidity", "Low", "Months"],
              ["Management effort", "High", "—"],
            ]} testid="reit-property"/>
          <ScenarioCard title="REIT (Embassy / Mindspace / Brookfield)" winner={winner === "reit"}
            metrics={[
              ["Capital deployed", inr(f.capital), "Full"],
              ["Loan taken", "—", "Not applicable"],
              ["Annual distribution", inr(r.annualDist), `${f.reit_yield}% yield`],
              [`End NAV (yr ${f.tenure_years})`, inr(r.reitEndNAV), null],
              ["Total return", inr(r.reitTotalReturn), null],
              ["Effective XIRR (approx)", `${r.reitXirr.toFixed(2)}%`, "p.a."],
              ["Liquidity", "High", "T+2 days"],
              ["Management effort", "None", "—"],
            ]} testid="reit-reit"/>
        </div>
        <div className="text-[10px] text-muted-foreground">
          REIT distributions in India: distribution from SPV's interest / dividend is taxable as
          'other income' at slab rate when SPV doesn't opt for §115BAA. Embassy/Mindspace/Brookfield
          past 5-year yields: 6-8% indicative. Past performance ≠ future returns.
        </div>
        <CalcDisclaimer calc="rental" />
      </div>
    </div>
  );
}

function ScenarioCard({ title, winner, metrics, testid }) {
  return (
    <div className={`card-flat p-5 border-2 ${winner ? "border-green-500" : "border-transparent"}`}
      data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <div className="font-serif text-lg">{title}</div>
        {winner && <span className="bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">✓ Higher XIRR</span>}
      </div>
      <div className="space-y-1.5 text-xs">
        {metrics.map(([k, v, sub]) => (
          <div key={k} className="flex items-baseline justify-between border-b hairline pb-1.5">
            <div>
              <div className="text-muted-foreground">{k}</div>
              {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
            </div>
            <div className="font-medium">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================================================================== */
/* P08 — Affordability (bank-wise)                                          */
/* ======================================================================== */
export function AffordabilityCalc() {
  const [form, setForm] = useState({
    net_monthly_income: 150000, coapplicant_net_income: 0, existing_emis: 0,
    tenure_years: 20, down_payment: 1000000, gender: "male",
  });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refMeta, setRefMeta] = useState(null);
  // Pull lender freshness metadata once
  useEffect(() => {
    api.get("/calc/reference/lenders").then((r) => setRefMeta(r.data)).catch(() => {});
  }, []);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calc/affordability/bank", form);
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  const shareMsg = res ? `🏠 Bank-wise Affordability (via Estima):
Income: ${inr(form.net_monthly_income)}/mo
Best max loan: ${inr(Math.max(...res.rows.map(r => r.max_loan)))}
Compare 5 lenders → estima.co.in` : "";
  return (
    <div className="space-y-6" data-testid="afford-calc">
      <CalcHeader calc="affordability" title="Affordability — Bank-wise Comparison"
        subtitle="Top 5 lenders' max loan, max property and EMI given your income. Updated June 2026.">
        {res && <WhatsAppShare calc="affordability" message={shareMsg}/>}
      </CalcHeader>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="card-flat p-5 space-y-3">
          <NumField label="Net monthly income (₹)" value={form.net_monthly_income}
            onChange={(v) => setForm({...form, net_monthly_income: v})}
            testid="afford-income"/>
          <NumField label="Co-applicant income (₹)" value={form.coapplicant_net_income}
            onChange={(v) => setForm({...form, coapplicant_net_income: v})}/>
          <NumField label="Existing EMIs (₹)" value={form.existing_emis}
            onChange={(v) => setForm({...form, existing_emis: v})}
            tooltip="Sum of all active loan EMIs"/>
          <NumField label="Loan tenure (years)" value={form.tenure_years}
            onChange={(v) => setForm({...form, tenure_years: v})}/>
          <NumField label="Down payment (₹)" value={form.down_payment}
            onChange={(v) => setForm({...form, down_payment: v})}/>
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Gender (for SBI women rate)</span>
            <div className="inline-flex border hairline rounded overflow-hidden text-xs">
              {[["male", "Male"], ["female", "Female"]].map(([v, l]) => (
                <button key={v} onClick={() => setForm({...form, gender: v})}
                  data-testid={`afford-gender-${v}`}
                  className={`px-3 py-1.5 ${form.gender === v ? "bg-[hsl(var(--primary))] text-white" : ""}`}>{l}</button>
              ))}
            </div>
          </label>
          <button onClick={run} disabled={loading} className="btn-primary w-full py-2.5 text-sm mt-2"
            data-testid="afford-run">{loading ? "Calculating…" : "Compare 5 lenders"}</button>
        </div>
        <div>
          {!res ? (
            <div className="card-flat p-8 text-muted-foreground">Enter your income, hit Compare.</div>
          ) : (
            <div className="space-y-3" data-testid="afford-result">
              {refMeta?.stale && (
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-700 text-xs p-3"
                  data-testid="afford-stale-warning">
                  ⚠ Lender rates were last refreshed {refMeta.months_since_update} months ago ({refMeta.as_of}).
                  Rates may have changed — confirm with the bank before applying.
                </div>
              )}
              <div className="card-flat p-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b hairline">
                      <th className="text-left p-2 sticky left-0 bg-background">Metric</th>
                      {res.rows.map((r) => (
                        <th key={r.id} className="text-right p-2 min-w-[110px]">
                          {r.name}
                          {res.best.lowest_rate === r.id && <span className="block text-[10px] text-green-700">⭐ Best rate</span>}
                          {res.best.highest_loan === r.id && <span className="block text-[10px] text-green-700">⭐ Max loan</span>}
                          {res.best.lowest_total === r.id && <span className="block text-[10px] text-green-700">⭐ Cheapest total</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody data-testid="afford-rows">
                    <CmpRow k="Rate (%)" rows={res.rows} get={(r) => `${r.effective_rate_pct}%`} bestId={res.best.lowest_rate}/>
                    <CmpRow k="Max EMI capacity" rows={res.rows} get={(r) => inr(r.max_emi)}/>
                    <CmpRow k="Max loan eligible" rows={res.rows} get={(r) => inr(r.max_loan)} bestId={res.best.highest_loan}/>
                    <CmpRow k="Max property value" rows={res.rows} get={(r) => inr(r.max_property_value)} bestId={res.best.highest_property}/>
                    <CmpRow k="Processing fee" rows={res.rows} get={(r) => inr(r.processing_fee)}/>
                    <CmpRow k="GST on fee" rows={res.rows} get={(r) => inr(r.gst_on_fee)}/>
                    <CmpRow k="Total loan cost" rows={res.rows} get={(r) => inr(r.total_loan_cost)} bestId={res.best.lowest_total}/>
                  </tbody>
                </table>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                {res.rows.map((r) => (
                  <a key={r.id} href={r.url} target="_blank" rel="noreferrer"
                    className="border hairline p-3 hover:bg-muted/30">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground">{r.perks}</div>
                    <div className="text-[10px] text-[hsl(var(--primary))] mt-1">Apply / pre-approval →</div>
                  </a>
                ))}
              </div>
              <CalcDisclaimer calc="affordability"/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CmpRow({ k, rows, get, bestId }) {
  return (
    <tr className="border-b hairline">
      <td className="p-2 text-muted-foreground sticky left-0 bg-background">{k}</td>
      {rows.map((r) => (
        <td key={r.id}
          className={`p-2 text-right ${r.id === bestId ? "bg-green-500/15 text-green-700 font-medium" : ""}`}>
          {get(r)}
        </td>
      ))}
    </tr>
  );
}

/* ======================================================================== */
/* Reusable Metric component (matches Calculators.jsx pattern)              */
/* ======================================================================== */
function Metric({ label, value, accent, testid }) {
  return (
    <div className="card-flat p-4">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent === "good" ? "text-green-700" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

/* ======================================================================== */
/* P06 — Step-Up EMI Planner — embedded in EMI tab (called from Calculators.jsx) */
/* ======================================================================== */
export function StepUpEmiPlanner({ baseLoan, baseRate, baseTenureYears, baseEmi }) {
  const [stepUp, setStepUp] = useState(5);
  const r = useMemo(() => {
    if (!baseLoan || !baseRate || !baseTenureYears) return null;
    const monthlyRate = baseRate / 100 / 12;
    let outstanding = baseLoan;
    let currentEmi = baseEmi;
    let totalInterest = 0;
    let closureMonth = 0;
    const yearly = [];
    for (let y = 0; y < baseTenureYears && outstanding > 0; y++) {
      const yearInterestRows = [];
      let interestThisYear = 0;
      let principalThisYear = 0;
      for (let m = 0; m < 12 && outstanding > 0; m++) {
        const intMonth = outstanding * monthlyRate;
        let principalMonth = currentEmi - intMonth;
        if (principalMonth > outstanding) principalMonth = outstanding;
        outstanding -= principalMonth;
        interestThisYear += intMonth;
        principalThisYear += principalMonth;
        totalInterest += intMonth;
        closureMonth = y * 12 + m + 1;
        yearInterestRows.push(intMonth);
      }
      yearly.push({ year: y + 1, emi: Math.round(currentEmi), interest: Math.round(interestThisYear),
                    principal: Math.round(principalThisYear), closing: Math.max(0, Math.round(outstanding)) });
      if (outstanding <= 0) break;
      currentEmi = currentEmi * (1 + stepUp / 100);
    }
    // Flat baseline for comparison
    const flatTotal = baseEmi * baseTenureYears * 12;
    const flatInterest = flatTotal - baseLoan;
    return { yearly, totalInterest, closureMonth, monthsSaved: baseTenureYears * 12 - closureMonth,
             interestSaved: flatInterest - totalInterest, flatInterest };
  }, [baseLoan, baseRate, baseTenureYears, baseEmi, stepUp]);

  if (!r) return null;
  return (
    <div className="card-flat p-5 mt-6" data-testid="stepup-emi-planner">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="eyebrow inline-flex items-center gap-2">
          <TrendUp size={12} weight="duotone"/> Step-Up EMI Planner
        </div>
        <div className="text-[10px] text-muted-foreground">India first — pay less interest with annually-rising EMIs.</div>
      </div>
      <div className="flex items-end gap-3 mb-4">
        <label className="text-xs space-y-1 block">
          <span className="text-muted-foreground">Annual step-up %</span>
          <input type="number" value={stepUp} step="1" min={1} max={15}
            onChange={(e) => setStepUp(Number(e.target.value))}
            className="w-24 bg-background border hairline px-2 py-1.5 text-xs"
            data-testid="stepup-rate"/>
        </label>
        <div className="text-xs text-muted-foreground">Suggested 5-8% to match salary growth</div>
      </div>
      <div className="grid md:grid-cols-4 gap-3 text-xs mb-4">
        <Metric label="Total interest (flat)" value={inr(r.flatInterest)}/>
        <Metric label="Total interest (step-up)" value={inr(r.totalInterest)} accent="good"/>
        <Metric label="Interest saved" value={inr(r.interestSaved)} accent="good" testid="stepup-savings"/>
        <Metric label="Loan closes" value={`Month ${r.closureMonth}`} testid="stepup-closure"/>
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={r.yearly}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4"/>
            <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))"/>
            <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => inr(v)}/>
            <Tooltip formatter={(v) => inrFull(v)}/>
            <Bar dataKey="emi" fill="#0B7B6E" name="Year EMI"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2">
        Tip: SBI, HDFC and Axis offer Step-Up / Tranche EMI products for salaried borrowers.
      </div>
    </div>
  );
}
